import React, { useEffect, useState } from "react";
import DashboardLayout from "@/components/DashboardLayout";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { HeartHalf, ChartLineUp, Users, FloppyDisk, ListChecks } from "@phosphor-icons/react";
import { toast } from "sonner";

const fmtINR = (n) => new Intl.NumberFormat("en-IN", { style: "currency", currency: "INR", maximumFractionDigits: 0 }).format(Number(n) || 0);

export default function SacHome() {
  const [profiles, setProfiles] = useState([]);
  const [report, setReport] = useState(null);
  const [settings, setSettings] = useState(null);
  const [allotment, setAllotment] = useState("");
  const [busy, setBusy] = useState(false);

  const refresh = () => Promise.all([
    api.get("/sac/profiles").then((r) => setProfiles(r.data || [])),
    api.get("/sac/reports/yearly").then((r) => setReport(r.data)),
    api.get("/admin/settings/sac").then((r) => { setSettings(r.data); setAllotment(String(r.data.allotment_amount ?? "")); }),
  ]).catch(() => {});
  useEffect(() => { refresh(); }, []);

  const saveAllotment = async () => {
    setBusy(true);
    try {
      await api.put("/admin/settings/sac", { allotment_amount: Number(allotment) || 0 });
      toast.success("Allotment updated — applies to every SAC child");
      refresh();
    } catch (e) { toast.error(formatApiErrorDetail(e?.response?.data?.detail) || e.message); }
    finally { setBusy(false); }
  };

  const active = profiles.filter((p) => p.status === "active");
  const dropped = profiles.filter((p) => p.status === "dropped");

  return (
    <DashboardLayout title="Support a Child" subtitle="Sponsorship roster, allotments, and year-wise totals">
      <div className="grid grid-cols-1 sm:grid-cols-4 gap-3 mb-5">
        <div className="vs-card p-4" data-testid="sac-stat-active">
          <div className="eyebrow flex items-center gap-1.5"><Users size={11} /> Active children</div>
          <div className="font-display text-3xl font-semibold mt-1">{active.length}</div>
        </div>
        <div className="vs-card p-4" data-testid="sac-stat-dropped">
          <div className="eyebrow">Dropped this cycle</div>
          <div className="font-display text-3xl font-semibold mt-1">{dropped.length}</div>
        </div>
        <div className="vs-card p-4" data-testid="sac-stat-allotment">
          <div className="eyebrow flex items-center gap-1.5"><HeartHalf size={11} /> Allotment per child</div>
          <div className="font-display text-2xl font-semibold mt-1">{fmtINR(settings?.allotment_amount)}</div>
        </div>
        <div className="vs-card p-4" data-testid="sac-stat-total">
          <div className="eyebrow flex items-center gap-1.5"><ChartLineUp size={11} /> Total commitment</div>
          <div className="font-display text-2xl font-semibold mt-1">{fmtINR(report?.grand_total)}</div>
        </div>
      </div>

      <div className="vs-card p-5 mb-5">
        <div className="eyebrow mb-2">Central allotment</div>
        <p className="text-sm text-[var(--vs-text-secondary)] mb-3">Set once — applies to every SAC child for the current cycle. No per-child override.</p>
        <div className="flex items-end gap-3 max-w-md">
          <div className="flex-1">
            <Label htmlFor="allotment">Amount per child (₹)</Label>
            <Input id="allotment" type="number" min="0" value={allotment} onChange={(e) => setAllotment(e.target.value)} data-testid="sac-allotment-input" />
          </div>
          <Button onClick={saveAllotment} disabled={busy} data-testid="sac-allotment-save" className="bg-[var(--vs-secondary)] text-[var(--vs-bg)]">
            <FloppyDisk size={14} className="mr-1.5" /> Save
          </Button>
        </div>
      </div>

      <Tabs defaultValue="active">
        <TabsList>
          <TabsTrigger value="active" data-testid="sac-tab-active">Active ({active.length})</TabsTrigger>
          <TabsTrigger value="dropped" data-testid="sac-tab-dropped">Dropped ({dropped.length})</TabsTrigger>
          <TabsTrigger value="report" data-testid="sac-tab-report"><ListChecks size={12} className="mr-1.5" /> Year-wise totals</TabsTrigger>
        </TabsList>
        <TabsContent value="active">
          <SacTable rows={active} onRefresh={refresh} />
        </TabsContent>
        <TabsContent value="dropped">
          <SacTable rows={dropped} onRefresh={refresh} />
        </TabsContent>
        <TabsContent value="report">
          <YearlyReport series={report?.series || []} />
        </TabsContent>
      </Tabs>
    </DashboardLayout>
  );
}

function SacTable({ rows, onRefresh }) {
  if (!rows.length) {
    return <div className="vs-card p-10 text-center text-[var(--vs-text-secondary)]" data-testid="sac-empty">No children yet.</div>;
  }
  return (
    <div className="vs-card overflow-x-auto" data-testid="sac-profiles-table">
      <table className="w-full text-sm">
        <thead className="bg-[var(--vs-bg-subtle)]">
          <tr className="text-left text-[11px] uppercase font-mono tracking-widest text-[var(--vs-text-secondary)]">
            <th className="px-4 py-3">Child</th>
            <th className="px-4 py-3">Class</th>
            <th className="px-4 py-3">% (current)</th>
            <th className="px-4 py-3">Wants to be</th>
            <th className="px-4 py-3">FY</th>
            <th className="px-4 py-3">Status</th>
            <th className="px-4 py-3"></th>
          </tr>
        </thead>
        <tbody>
          {rows.map((p) => (
            <tr key={p.id} className="border-t border-[var(--vs-border)]" data-testid={`sac-row-${p.student_id}`}>
              <td className="px-4 py-3">
                <div className="font-medium">{p.first_name} {p.last_name}</div>
                <div className="text-xs text-[var(--vs-text-secondary)] font-mono">{p.student_id?.slice(0, 8)}</div>
              </td>
              <td className="px-4 py-3 font-mono">{p.class_name || "—"}</td>
              <td className="px-4 py-3 font-mono">{p.pct_obtained_current ?? "—"}</td>
              <td className="px-4 py-3">{p.what_child_wants_to_be || "—"}</td>
              <td className="px-4 py-3 font-mono text-xs">{p.financial_year}</td>
              <td className="px-4 py-3">
                <Badge variant="outline" className={p.status === "active"
                  ? "border-[var(--vs-success)]/40 text-[var(--vs-success)] bg-[var(--vs-success)]/10"
                  : "border-[var(--vs-text-secondary)]/30 text-[var(--vs-text-secondary)]"}>
                  {p.status}
                </Badge>
              </td>
              <td className="px-4 py-3 text-right">
                <SacProfileView profile={p} />
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function SacProfileView({ profile }) {
  return (
    <Dialog>
      <DialogTrigger asChild>
        <Button size="sm" variant="outline" data-testid={`sac-view-${profile.student_id}`}>View profile</Button>
      </DialogTrigger>
      <DialogContent className="max-w-2xl">
        <DialogHeader><DialogTitle>{profile.first_name} {profile.last_name} — SAC profile</DialogTitle></DialogHeader>
        <div className="grid grid-cols-2 gap-3 text-sm">
          {[
            ["DOB", profile.dob], ["Gender", profile.gender],
            ["Father", profile.father_name], ["Mother", profile.mother_name],
            ["State", profile.state], ["District", profile.district],
            ["City", profile.city], ["Pincode", profile.pincode],
            ["Class", profile.class_name],
            ["% current", profile.pct_obtained_current], ["% previous", profile.pct_obtained_previous],
            ["Favourite subject", profile.favourite_subject], ["Special talent", profile.special_talent],
            ["Achievement", profile.any_achievement], ["Wants to be", profile.what_child_wants_to_be],
          ].map(([k, v]) => (
            <div key={k}>
              <div className="eyebrow">{k}</div>
              <div className="mt-0.5">{v || <span className="text-[var(--vs-text-secondary)]">—</span>}</div>
            </div>
          ))}
        </div>
        <div className="vs-card p-3 mt-3">
          <div className="eyebrow mb-1">Full address</div>
          <div className="text-sm">{profile.full_address || "—"}</div>
        </div>
        {profile.yearly_history?.length > 0 && (
          <div className="vs-card p-3 mt-3">
            <div className="eyebrow mb-2">Year-wise history</div>
            <ul className="text-xs font-mono space-y-1">
              {profile.yearly_history.map((h, i) => (
                <li key={`${h.fy}-${h.action}-${h.at || i}`}>{h.fy} — {h.action}{h.new_class ? ` → class ${h.new_class}` : ""}{h.reason ? ` (${h.reason})` : ""}</li>
              ))}
            </ul>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

function YearlyReport({ series }) {
  if (!series.length) {
    return <div className="vs-card p-10 text-center text-[var(--vs-text-secondary)]">No data yet.</div>;
  }
  const grandTotal = series.reduce((s, r) => s + (r.total || 0), 0);
  return (
    <div className="vs-card overflow-x-auto" data-testid="sac-yearly-report">
      <table className="w-full text-sm">
        <thead className="bg-[var(--vs-bg-subtle)]">
          <tr className="text-left text-[11px] uppercase font-mono tracking-widest text-[var(--vs-text-secondary)]">
            <th className="px-4 py-3">Financial year</th>
            <th className="px-4 py-3">Children</th>
            <th className="px-4 py-3">Allotment / child</th>
            <th className="px-4 py-3">Total</th>
          </tr>
        </thead>
        <tbody>
          {series.map((r) => (
            <tr key={r.financial_year} className="border-t border-[var(--vs-border)]">
              <td className="px-4 py-3 font-mono">{r.financial_year}</td>
              <td className="px-4 py-3 font-mono">{r.count}</td>
              <td className="px-4 py-3 font-mono">{fmtINR(r.allotment_per_child)}</td>
              <td className="px-4 py-3 font-display font-semibold">{fmtINR(r.total)}</td>
            </tr>
          ))}
          <tr className="border-t-2 border-[var(--vs-border)] bg-[var(--vs-bg-subtle)]">
            <td className="px-4 py-3 font-semibold">Total commitment</td>
            <td colSpan={2}></td>
            <td className="px-4 py-3 font-display font-bold text-lg">{fmtINR(grandTotal)}</td>
          </tr>
        </tbody>
      </table>
    </div>
  );
}
